import { useEffect, useState } from "react";
import { Save, Sparkles, Bot, Send, Coins, Check, AlertTriangle, Globe, RefreshCw } from "lucide-react";
import { api, cx, formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";

const PRODUCTION_URL = "https://meetandmint.com";

/** Admin: tune economic levers (signup grant, AI cost, automation cost). */
export default function AdminSettingsPanel() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [tg, setTg] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const [tgUrlOverride, setTgUrlOverride] = useState("");
  const [tgUrlInited, setTgUrlInited] = useState(false);

  const reload = async () => {
    setError(null);
    try {
      const [{ data: settingsRes }, tgRes] = await Promise.all([
        api.get("/admin/settings"),
        api.get("/admin/integrations/telegram/health").catch(() => ({ data: null })),
      ]);
      setData(settingsRes.settings);
      setTg(tgRes.data);
      // Pre-fill the override input the first time. We never surface
      // emergentagent.com URLs in the admin — if the registered webhook
      // points at a preview pod, default the input to production instead.
      if (!tgUrlInited) {
        let nextBase = PRODUCTION_URL;
        const u = tgRes.data?.webhook?.url;
        if (u) {
          try {
            const origin = new URL(u).origin;
            if (origin && !origin.includes("emergentagent.com")) {
              nextBase = origin;
            }
          } catch { /* ignore */ }
        }
        setTgUrlOverride(nextBase);
        setTgUrlInited(true);
      }
    } catch (e) { setError(formatErr(e)); }
  };
  useEffect(() => { reload(); }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const registerWebhook = async () => {
    setTgBusy(true); setError(null);
    try {
      const base = (tgUrlOverride || "").trim().replace(/\/+$/, "");
      const payload = base ? { url: base } : {};
      await api.post("/admin/integrations/telegram/set-webhook", payload);
      setFlash(t("admin_settings.tg_webhook_registered", "Webhook registered."));
      setTimeout(() => setFlash(null), 3500);
      const tgRes = await api.get("/admin/integrations/telegram/health");
      setTg(tgRes.data);
    } catch (e) { setError(formatErr(e)); }
    finally { setTgBusy(false); }
  };

  const save = async () => {
    if (!data) return;
    setBusy(true); setError(null);
    try {
      const { data: res } = await api.patch("/admin/settings", {
        mint_signup_grant: Number(data.mint_signup_grant) || 0,
        mint_per_1k_ai_tokens: Number(data.mint_per_1k_ai_tokens) || 0,
        team_automation_message_cost_mint: Number(data.team_automation_message_cost_mint) || 0,
      });
      setData(res.settings);
      setFlash(t("admin_settings.saved", "Settings saved."));
      setTimeout(() => setFlash(null), 3000);
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="luxe-card p-10 text-muted text-sm">{t("common.loading", "Loading…")}</div>;

  const rate = Number(data.mint_eur_rate || 0.01);
  const grantEur = (Number(data.mint_signup_grant) || 0) * rate;
  const aiEur = (Number(data.mint_per_1k_ai_tokens) || 0) * rate;
  const teamEur = (Number(data.team_automation_message_cost_mint) || 0) * rate;

  return (
    <div className="space-y-6" data-testid="admin-settings-panel">
      <div>
        <h2 className="font-serif-display text-3xl">{t("admin_settings.title", "Economics & settings")}</h2>
        <p className="text-sm text-muted mt-2 max-w-3xl">
          {t("admin_settings.subtitle", "Tune the levers behind MINT, AI cost and team automations. Changes apply immediately to new signups and dispatches.")}
        </p>
        <div className="mt-3 chip text-[11px] inline-flex" data-testid="admin-settings-rate">
          <Coins size={11} /> {t("admin_settings.current_rate_label", "Current MINT rate")} · {t("admin_settings.current_rate_value", "1 MINT ≈ €{{rate}}", { rate: rate.toFixed(2) })}
        </div>
      </div>

      {error && <div className="text-rose-300 text-sm" data-testid="settings-error">{error}</div>}
      {flash && <div className="luxe-card p-3 text-sm text-teal border-teal/40" data-testid="settings-flash">{flash}</div>}

      <div className="grid lg:grid-cols-3 gap-4">
        <SettingCard
          icon={<Sparkles size={16} className="text-teal" />}
          label={t("admin_settings.field_signup_grant", "MINT credited on new user signup")}
          hint={t("admin_settings.field_signup_grant_hint", "Free welcome bonus. €0.01 per MINT by default → 500 MINT = €5 of credit.")}
          value={data.mint_signup_grant}
          onChange={(v) => set("mint_signup_grant", v)}
          eur={grantEur}
          testid="setting-signup-grant"
        />
        <SettingCard
          icon={<Bot size={16} className="text-teal" />}
          label={t("admin_settings.field_ai_cost", "MINT per 1,000 AI tokens")}
          hint={t("admin_settings.field_ai_cost_hint", "Deducted from the user's MINT balance for every 1k tokens used by the AI concierge / agents.")}
          value={data.mint_per_1k_ai_tokens}
          onChange={(v) => set("mint_per_1k_ai_tokens", v)}
          eur={aiEur}
          step="0.5"
          testid="setting-ai-cost"
        />
        <SettingCard
          icon={<Send size={16} className="text-teal" />}
          label={t("admin_settings.field_team_cost", "MINT per automated team message")}
          hint={t("admin_settings.field_team_cost_hint", "Deducted from the provider's MINT balance whenever an automation rule dispatches a message.")}
          value={data.team_automation_message_cost_mint}
          onChange={(v) => set("team_automation_message_cost_mint", v)}
          eur={teamEur}
          step="0.5"
          testid="setting-team-cost"
        />
      </div>

      <div>
        <button
          onClick={save}
          disabled={busy}
          className={cx("btn btn-primary", busy && "opacity-60")}
          data-testid="btn-save-settings"
        >
          <Save size={14} /> {t("admin_settings.save", "Save settings")}
        </button>
      </div>

      {/* Telegram health */}
      <section className="pt-4" data-testid="admin-tg-health-section">
        <h3 className="font-serif-display text-2xl mb-1">{t("admin_settings.tg_title", "Telegram bot")}</h3>
        <p className="text-sm text-muted mb-4 max-w-3xl">
          {t("admin_settings.tg_subtitle", "End-to-end status for the team automation channel. Re-register the webhook after a domain change or if pending updates start piling up.")}
        </p>
        <TelegramHealthCard
          tg={tg}
          onRegister={registerWebhook}
          busy={tgBusy}
          urlOverride={tgUrlOverride}
          setUrlOverride={setTgUrlOverride}
          t={t}
        />
      </section>
    </div>
  );
}

function TelegramHealthCard({ tg, onRegister, busy, urlOverride, setUrlOverride, t }) {
  if (!tg) {
    return <div className="luxe-card p-4 text-sm text-muted">{t("common.loading", "Loading…")}</div>;
  }
  const ok = tg.ok && !!(tg.webhook?.url);
  const pending = tg.webhook?.pending_update_count || 0;
  const last_error = tg.webhook?.last_error_message;
  const last_error_date = tg.webhook?.last_error_date;
  const cleaned = (urlOverride || "").trim().replace(/\/+$/, "");
  const valid = /^https:\/\//i.test(cleaned);
  const computedFull = valid ? `${cleaned}/api/integrations/telegram/webhook` : "";
  const willPointAtProd = cleaned === PRODUCTION_URL;
  const currentRegisteredOrigin = (() => {
    try { return tg.webhook?.url ? new URL(tg.webhook.url).origin : ""; } catch { return ""; }
  })();
  const wouldChange = currentRegisteredOrigin && cleaned && currentRegisteredOrigin !== cleaned;

  return (
    <div className="luxe-card p-5 space-y-4" data-testid="admin-tg-health-card">
      <div className="flex items-center gap-3 flex-wrap">
        {ok ? (
          <span className="chip success text-[11px]" data-testid="tg-health-ok"><Check size={11} /> {t("admin_settings.tg_status_ok", "Live")}</span>
        ) : tg.ok ? (
          <span className="chip warning text-[11px]" data-testid="tg-health-no-webhook"><AlertTriangle size={11} /> {t("admin_settings.tg_status_no_webhook", "Webhook not registered")}</span>
        ) : (
          <span className="chip danger text-[11px]" data-testid="tg-health-fail"><AlertTriangle size={11} /> {t("admin_settings.tg_status_fail", "Bot token misconfigured")}</span>
        )}
        {tg.bot_username && (
          <span className="chip text-[11px]"><Bot size={11} /> @{tg.bot_username}</span>
        )}
        {pending > 0 && (
          <span className="chip warning text-[11px]" data-testid="tg-health-pending">
            {t("admin_settings.tg_pending_updates", "{{n}} updates queued", { n: pending })}
          </span>
        )}
        {currentRegisteredOrigin === PRODUCTION_URL && (
          <span className="chip success text-[11px]">production</span>
        )}
      </div>
      {tg.webhook?.url && !tg.webhook.url.includes("emergentagent.com") && (
        <div className="text-[11px] text-muted/80 font-mono break-all flex items-start gap-2" data-testid="tg-webhook-url">
          <Globe size={11} className="shrink-0 mt-0.5" />
          <span>{tg.webhook.url}</span>
        </div>
      )}
      {last_error && (
        <div className="luxe-card p-3 border-rose-300/40 bg-rose-500/5 text-rose-200 text-xs" data-testid="tg-last-error">
          <div className="font-mono">{last_error}</div>
          {last_error_date && <div className="text-rose-200/70 mt-1">{new Date(last_error_date * 1000).toISOString()}</div>}
        </div>
      )}

      {/* URL override + register controls */}
      <div className="luxe-card p-4 space-y-3 border-teal/20 bg-teal/[0.03]" data-testid="tg-set-webhook-block">
        <div>
          <span className="label-tiny block mb-1.5">{t("admin_settings.tg_url_label", "Webhook base URL")}</span>
          <input
            type="url"
            value={urlOverride}
            onChange={(e) => setUrlOverride(e.target.value)}
            placeholder="https://meetandmint.com"
            className={cx("input-bare font-mono text-[13px]", !valid && cleaned && "border-rose-400/40")}
            data-testid="tg-url-input"
          />
          <div className="text-[11px] text-muted/70 mt-1.5">
            {t("admin_settings.tg_url_hint", "Telegram requires HTTPS. We'll POST to <base>/api/integrations/telegram/webhook.")}
          </div>
        </div>

        {valid && !cleaned.includes("emergentagent.com") && (
          <div className="text-[11px] text-muted/80 font-mono break-all flex items-start gap-2 pl-1" data-testid="tg-url-preview">
            <Globe size={11} className="shrink-0 mt-0.5 text-teal" />
            <span>{computedFull}</span>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={() => setUrlOverride(PRODUCTION_URL)}
            className={cx("chip text-[11px] cursor-pointer hover:border-teal/60", cleaned === PRODUCTION_URL && "border-teal/60 text-teal")}
            data-testid="tg-shortcut-prod"
          >
            production · meetandmint.com
          </button>
        </div>

        {wouldChange && currentRegisteredOrigin === PRODUCTION_URL && (
          <div className="luxe-card p-3 border-champagne/40 bg-champagne/5 text-xs text-champagne" data-testid="tg-change-warning">
            <AlertTriangle size={12} className="inline-block mr-1.5 -mt-0.5" />
            {t("admin_settings.tg_change_warning_prod",
              "Heads-up: this will move the bot away from production. Real users on meetandmint.com will stop receiving Telegram replies.",
            )}
          </div>
        )}

        <div>
          <button
            type="button"
            onClick={onRegister}
            disabled={busy || !valid}
            className={cx(
              "btn text-[12px]",
              willPointAtProd ? "btn-primary" : "btn-outline",
              (busy || !valid) && "opacity-60",
            )}
            data-testid="btn-tg-register-webhook"
          >
            <RefreshCw size={12} />
            {willPointAtProd
              ? t("admin_settings.tg_register_prod", "Register on production")
              : t("admin_settings.tg_register_webhook", "Register / refresh webhook")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SettingCard({ icon, label, hint, value, onChange, eur, step = "1", testid }) {
  return (
    <div className="luxe-card p-5 space-y-3" data-testid={testid}>
      <div className="flex items-center gap-2">{icon}<span className="label-tiny">{label}</span></div>
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-bare font-serif-display text-2xl"
        data-testid={`${testid}-input`}
      />
      <div className="text-[11px] text-muted/70 font-mono">≈ €{Number(eur).toFixed(2)}</div>
      <p className="text-xs text-muted">{hint}</p>
    </div>
  );
}
